import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react'
import {
  IconArrowAutofitContent,
  IconPlaylistAdd,
  IconShoppingBag,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { Button } from '~/components/Button'
import { SaveProductTooltip } from '~/components/SaveProductTooltip'
import { Snackbar } from '~/components/Snackbar'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { pluralize } from '~/helpers/pluralize'
import {
  useAlgoliaSearchInsignts,
  useCart,
  useCartDrawer,
  useCompareProducts,
  usePage,
} from '~/hooks'
import styles from './CompareProductsSnackbar.module.css'

const RESTRICTION_URLS = ['/checkout', '/compare']

export const CompareProductsSnackbar = () => {
  const {
    selectedProductIds,
    productsCount,
    maxProductsCount,
    clearAllProducts,
    selectedProductsList,
    dispatchAddToProductComparison,
  } = useCompareProducts()
  const { addToCart } = useCart()
  const { sendAddToCartEvent } = useAlgoliaSearchInsignts()
  const router = useRouter()
  const [isDomLoaded, setIsDomLoaded] = useState(false)
  const { openCartDrawer } = useCartDrawer()

  useEffect(() => {
    setIsDomLoaded(true)
  }, [])

  const handleAddToCart = () => {
    const skusAddedToCart: string[] = []
    const cartData = selectedProductIds.map((id) => {
      skusAddedToCart.push(id)
      return { quantity: 1, sku: id }
    })
    addToCart.mutate(cartData, {
      onSuccess(data) {
        sendAddToCartEvent(skusAddedToCart, data.lineItems, data.totalPrice)
        openCartDrawer()
      },
    })
    clearAllProducts()
  }

  const productList = selectedProductsList || []

  const isSelectedProductsInStock = productList?.every((product) => {
    return product?.inStock
  })

  const productsToBeCompared = productList?.filter((product) => {
    return selectedProductIds.includes(product?.sku)
  })

  const renderClearButton = useCallback(() => {
    return (
      <Button
        theme={{
          root: classNames(styles.button, '!bg-transparent !border-0 !px-0'),
        }}
        onClick={() => {
          return clearAllProducts()
        }}>
        <Typography
          tag={TypographyTag.p}
          variant={TypographyVariant.BodyRegular}>
          <span className='underline tracking-normal text-white'>
            Clear selection
          </span>
        </Typography>
      </Button>
    )
  }, [clearAllProducts])

  const isVisableBar = useMemo(() => {
    return (
      !RESTRICTION_URLS.some((url) => {
        return router.asPath.startsWith(url)
      }) &&
      selectedProductIds?.length > 1 &&
      isDomLoaded
    )
  }, [router, isDomLoaded, selectedProductIds])

  const selection = useMemo(() => {
    if (!isDomLoaded) {
      return '0 Product selected'
    }
    return `${pluralize(productsCount || 0, 'Product')} selected`
  }, [productsCount, isDomLoaded])

  const page = usePage()
  const isRTM = page?.brand === 'rtm'
  const outlineBtnStyle = { root: classNames(styles.button, styles.outlineBtn) }

  return (
    <Snackbar
      isVisible={isVisableBar}
      primary={
        <div className='flex items-center gap-x-4'>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegularBold}>
            {selection}
          </Typography>
          <div className='hidden md:block'>{renderClearButton()}</div>
        </div>
      }
      secondary={
        <div className='flex flex-col md:flex-row items-center gap-2'>
          <Button
            data-id='addToCartButton'
            theme={{ root: classNames(styles.button, styles.fillBtn) }}
            onClick={handleAddToCart}
            disabled={!isSelectedProductsInStock}>
            <IconShoppingBag />
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodySmallBold}>
              Add to Cart
            </Typography>
          </Button>
          <SaveProductTooltip
            wrapperClass={isRTM ? '' : 'w-full md:w-auto'}
            buttonProps={
              isRTM
                ? {
                    style: {
                      padding: '8px 10px 8px 10px',
                      height: '32px',
                      textTransform: 'none',
                      '--btn-solid-color-border-primary': 'white',
                      '--btn-solid-color-bg-primary': 'transparent',
                      '--btn-solid-color-text-primary': 'white',
                    } as CSSProperties,
                  }
                : {
                    theme: outlineBtnStyle,
                  }
            }
            clearAllProducts={clearAllProducts}
            toggle={
              <div
                className={classNames('flex items-center rounded h-8 gap-2.5')}>
                <IconPlaylistAdd size={14} />
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodySmallBold}>
                  Add to List
                </Typography>
              </div>
            }
            products={selectedProductsList}
          />
          <Button
            theme={outlineBtnStyle}
            href={`/compare?products=${selectedProductIds
              ?.filter((_, index) => {
                return index < maxProductsCount
              })
              .join('|')}`}
            onClick={() => {
              dispatchAddToProductComparison(productsToBeCompared)
            }}>
            <IconArrowAutofitContent />

            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodySmallBold}>
              Compare (Max {maxProductsCount})
            </Typography>
          </Button>
          <div className='md:hidden'>{renderClearButton()}</div>
        </div>
      }
    />
  )
}
